import ApiService from "../ApiService";
import { GlobalSearchService, type RecentlySearchedStore } from "./GlobalSearchService";
import { FolderSearchProvider } from "./providers/FolderSearchProvider";
import { FormSearchProvider } from "./providers/FormSearchProvider";
import { GroupSearchProvider } from "./providers/GroupSearchProvider.js";
import { MAX_SEARCH_RESULTS_PER_CATEGORY } from "../../constants.ts";
import { useLocalUserSettings, useLocalUserSettingsDispatch } from "../../state/localUserSettings";
import { useCustomerState } from "../../state/customer.tsx";
import UserService from "../UserService.ts";
import PermissionService from "../PermissionService.ts";
import { PageSearchProvider } from "./providers/PageSearchProvider.tsx";
import { DataSourceSearchProvider } from "./providers/DataSourceSearchProvider.tsx";
import { useTranslation } from "../../state/i18n.tsx";
import { UserSearchProvider } from "./providers/UserSearchProvider.tsx";

export const useGlobalSearch = () => {
  const { t } = useTranslation();
  const customer = useCustomerState();
  const foldersQuery = ApiService.getFolders();
  const localUserSettings = useLocalUserSettings();
  const setLocalUserSettings = useLocalUserSettingsDispatch();

  const recentlySearchedStore: RecentlySearchedStore = {
    get: () => localUserSettings.globalSearch?.recentlySearched[customer.customerId.toString()] ?? [],
    set: (recentlySearched) => {
      setLocalUserSettings("globalSearch", (prev) => ({
        ...prev,
        recentlySearched: { ...prev?.recentlySearched, [customer.customerId.toString()]: recentlySearched },
      }));
    },
  };
  const searchProviders = [
    new FolderSearchProvider(foldersQuery, MAX_SEARCH_RESULTS_PER_CATEGORY),
    new FormSearchProvider(customer.customerId, foldersQuery, MAX_SEARCH_RESULTS_PER_CATEGORY),
    ...(PermissionService.canManagePermissions(customer.customerId)
      ? [
          new GroupSearchProvider(UserService.getGroups(customer.customerId), MAX_SEARCH_RESULTS_PER_CATEGORY),
          new UserSearchProvider(
            (filters, size) => UserService.searchMembers(customer.customerId, filters, size),
            MAX_SEARCH_RESULTS_PER_CATEGORY,
          ),
        ]
      : []),
    ...(PermissionService.canManageDataSources(customer.customerId)
      ? [new DataSourceSearchProvider(ApiService.getDataSources(), MAX_SEARCH_RESULTS_PER_CATEGORY)]
      : []),
    new PageSearchProvider(t, customer.customerId, MAX_SEARCH_RESULTS_PER_CATEGORY),
  ];

  return new GlobalSearchService(recentlySearchedStore, ...searchProviders);
};
